import { defineStore } from 'pinia'
import { acl } from '@/acl'
import oauth2Service from '@/services/oauth2'
import { queryGet } from '@/queries/utils/request'

export interface User {
  id?: number
  name?: string
  email?: string
  tenants?: Tenant[]
}

export interface Tenant {
  id: number
  description: string
  active?: boolean
}

export interface AuthState {
  token: string | null
  refresh_token: string | null
  refreshing_token: boolean
  user: User | null
  currentTenant: Tenant | null
  status: {
    loggingIn?: boolean
    loggedIn?: boolean
  }
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    token: null,
    refresh_token: null,
    refreshing_token: false,
    user: null,
    currentTenant: null,
    status: {},
  }),

  getters: {
    isLoggedIn: (state): boolean => {
      return !!state.token && !!state.user
    },

    currentUser: (state): User | null => state.user,

    hasValidToken: (state): boolean => !!state.token && !!state.refresh_token,

    userEmail: (state): string | undefined => state.user?.email,

    userName: (state): string | undefined => state.user?.name,

    userId: (state): number | undefined => state.user?.id,
  },

  actions: {
    // OAuth login success - called after OAuth callback
    async oauthLoginSuccess(tokens: { access_token: string; refresh_token: string }): Promise<void> {
      this.status = { loggingIn: true }

      try {
        const { access_token, refresh_token } = tokens

        const user = await queryGet('/v2/users/me', {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        })

        // Filter for vendor tenant only (id = 0)
        const vendorTenant = user.tenants.find((tenant: Tenant) => tenant.id === 0)
        if (!vendorTenant) {
          this.logout()
          throw new Error("Você não tem acesso a essa aplicação")
        }

        // Replace user.tenants with only vendor tenant
        user.tenants = [vendorTenant]

        this.setLoginSuccess({
          user,
          token: access_token,
          refresh_token,
        })

        this.updateTenant(vendorTenant)
      } catch (err: any) {
        console.error(err)
        this.status = {}
        throw new Error(err.message || "Falha ao processar login OAuth2")
      }
    },

    // Refresh token
    async refreshToken(): Promise<{ access_token: string; refresh_token: string; user: User }> {
      if (!this.refresh_token) {
        throw new Error("No refresh token available")
      }

      this.refreshing_token = true

      try {
        const info = await oauth2Service.refreshToken(this.refresh_token)

        if (info.access_token) {
          const { access_token, refresh_token } = info
          const user = await queryGet('/v2/users/me', {
            headers: {
              Authorization: `Bearer ${access_token}`,
            },
          })

          // Filter for vendor tenant only (id = 0)
          const vendorTenant = user.tenants.find((tenant: Tenant) => tenant.id === 0)
          if (!vendorTenant) {
            this.logout()
            throw new Error("Você não tem acesso a essa aplicação")
          }

          // Replace user.tenants with only vendor tenant
          user.tenants = [vendorTenant]

          this.setLoginSuccess({
            user,
            token: access_token,
            refresh_token,
          })

          this.updateTenant(vendorTenant)

          return { access_token, refresh_token, user }
        } else {
          throw new Error("Refresh token failed")
        }
      } finally {
        this.refreshing_token = false
      }
    },

    // Set login success
    setLoginSuccess(authData: { user: User; token: string; refresh_token: string }): void {
      this.token = authData.token
      this.refresh_token = authData.refresh_token
      this.user = authData.user
      this.status = { loggedIn: true }
      // Reset ACL with new user permissions
      acl.reset(authData.user as { tenants: any[] })
    },

    // Logout
    logout(): void {
      this.clearAuthState()
      // Clear ACL permissions on logout
      acl.reset(null)
    },

    // Clear all auth state
    clearAuthState(): void {
      this.token = null
      this.refresh_token = null
      this.user = null
      this.currentTenant = null
      this.status = { loggedIn: false }
    },

    // Tenant management
    updateTenant(tenant: Tenant): void {
      this.currentTenant = {
        id: tenant.id,
        description: tenant.description,
      }
      acl.reset(this.user)
    },

    // Update tokens (for token refresh)
    updateTokens(tokens: { token: string; refresh_token: string }): void {
      this.token = tokens.token
      this.refresh_token = tokens.refresh_token
    },
  },

  persist: {
    key: 'auth',
    storage: localStorage,
    // Only persist essential auth data
    pick: ['token', 'refresh_token', 'user', 'currentTenant'],
  },
})
